import React, { useEffect, useState } from "react";
import { useMentorApi } from "@/api/MentorApi";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";

const MentorshipRequests: React.FC = () => {
  const { getAllRequests, respondToRequest } = useMentorApi();
  const [requests, setRequests] = useState<any[]>([]);
  const [isMentorMode, setIsMentorMode] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await getAllRequests();
      if (Array.isArray(data)) {
        setRequests(data);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des demandes", error);
    }
    setLoading(false);
  };

  const StatusBadge = ({ status }: { status: string }) => {
    let label = "En attente";
    let color = "bg-yellow-100 text-yellow-800";

    if (status === "active") {
      label = "Acceptée";
      color = "bg-green-100 text-green-800";
    } else if (status === "inactive") {
      label = "Rejetée";
      color = "bg-red-100 text-red-800";
    }

    return (
      <span
        className={`text-sm px-2 py-1 rounded-full font-medium border ${color}`}
      >
        {label}
      </span>
    );
  };

  const handleRespond = async (id: string, status: "active" | "inactive") => {
    await respondToRequest(id, status);
    fetchRequests();
  };

  useEffect(() => {
    if (isMentorMode) {
      fetchRequests();
    }
  }, [isMentorMode]);

  if (!isMentorMode) {
    return (
      <div className="p-6 text-center">
        <p className="mb-4 text-gray-600">Mode mentor désactivé.</p>
        <Switch checked={isMentorMode} onCheckedChange={setIsMentorMode} />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          Demandes de mentorat
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Mode mentor</span>
          <Switch checked={isMentorMode} onCheckedChange={setIsMentorMode} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <Loader2 className="animate-spin w-6 h-6 text-blue-500" />
        </div>
      ) : requests.length === 0 ? (
        <p className="text-gray-500 text-center">Aucune demande en attente.</p>
      ) : (
        <ul className="space-y-4">
          {requests.map((req) => (
            <li
              key={req.id}
              className="bg-white/40 backdrop-blur-md border border-gray-200 shadow-md rounded-2xl p-5 transition hover:shadow-lg hover:bg-white/60"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                <div>
                  <p className="font-semibold text-gray-800">
                    Mentor : {req.mentor_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    Du <span className="font-medium">{req.Start_Date}</span> au{" "}
                    <span className="font-medium">{req.End_Date}</span>
                  </p>
                  <div className="mt-1">
                    <StatusBadge status={req.status} />
                  </div>
                </div>
                <div className="flex gap-2 mt-2 md:mt-0">
                  <Button
                    className="bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => handleRespond(req.id, "active")}
                  >
                    Accepter
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleRespond(req.id, "inactive")}
                  >
                    Rejeter
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MentorshipRequests;
